import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Chip,
  IconButton,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { InfoOutlined, Clear as ClearIcon, ViewInArOutlined } from "@mui/icons-material";
import { useGetVehiclesWithLoadingStatusQuery } from "store/services/shipmentLoadingApi";
import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TextField } from "@mui/material";
import moment from "moment";
import { addDays, differenceInDays } from "date-fns";

const getStatusStyle = (status) => {
  switch (status) {
    case "loaded":
      return { backgroundColor: "#D1FAE5", textColor: "#065F46" }; // green
    case "not loaded":
      return { backgroundColor: "#E5E7EB", textColor: "#374151" }; // gray
    default:
      return { backgroundColor: "#FEF3C7", textColor: "#92400E" }; // amber for N/A
  }
};

const ShipmentLoadingByVehicleList = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [customFromDate, setCustomFromDate] = useState(
    searchParams.get("fromDate") ? new Date(searchParams.get("fromDate")) : null
  );
  const [customToDate, setCustomToDate] = useState(
    searchParams.get("toDate") ? new Date(searchParams.get("toDate")) : null
  );
  const [errorMessage, setErrorMessage] = useState("");

  const keyword = searchParams.get("search") || "";

  const { data, error, isLoading, isFetching } =
    useGetVehiclesWithLoadingStatusQuery(
      {
        vehicleId: keyword || undefined,
        status: status !== "all" ? status : undefined,
        fromDate: customFromDate
          ? moment(customFromDate).format("MM-DD-YYYY")
          : undefined,
        toDate: customToDate
          ? moment(customToDate).format("MM-DD-YYYY")
          : undefined,
        page: currentPage,
        limit: perPage,
      },
      { refetchOnMountOrArgChange: true }
    );

  const breadcrumbs = [
    { title: "Shipment Loading", path: "/app/crm/shipment-loading/list" },
    { title: "Vehicle Loading Status" },
  ];

  const columns = [
    {
      name: "Vehicle ID",
      selector: (row) => row.vehicle_id,
      cell: (row) => (
        <Link
          title={row?.vehicle_id}
          to={`/app/crm/vehicle/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.vehicle_id ? (
            <HighlightedText text={row?.vehicle_id} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Registration No.",
      selector: (row) => row.registration_number,
      cell: (row) => (
        <Link
          title={row?.registration_number}
          to={`/app/crm/vehicle/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.registration_number ? (
            <HighlightedText
              text={row?.registration_number}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Vehicle Type",
      selector: (row) => row.vehicleType?.name,
      cell: (row) => row.vehicleType?.name || "N/A",
    },
    {
      name: "Ownership",
      selector: (row) => row.owner?.name,
      cell: (row) => row.owner?.name || "N/A",
    },
    {
      name: "Capacity",
      selector: (row) => row.capacity,
      cell: (row) =>
        row.capacity ? `${row.capacity} ${row.unit || ""}` : "N/A",
    },

    {
      name: "Loaded Weight",
      selector: (row) => row.loadedWeight,
      cell: (row) => `${row.loadedWeight || 0} KG`,
    },

    {
      name: "Last Loaded",
      selector: (row) => row.latestLoading?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.latestLoading?.createdAt} />,
    },
    {
      name: "Status",
      selector: (row) => row.loadingStatus,
      cell: (row) => (
        <Chip
          label={row?.loadingStatus?.toUpperCase() || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getStatusStyle(row?.loadingStatus).backgroundColor,
            color: getStatusStyle(row?.loadingStatus).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": {
              fontSize: "0.6rem",
              padding: "3px 5px",
              lineHeight: "14px",
            },
          }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vehicle/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Vehicle" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/vehicle/${row?._id}/shipment-detail-by-vehicle`}>
            <IconButton size="small" aria-label="View Vehicle" color="primary">
              <ViewInArOutlined />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const handleSearch = useCallback(
    (searchValue) => {
      const params = new URLSearchParams(searchParams);
      if (searchValue && searchValue.trim()) {
        params.set("search", searchValue.trim());
      } else {
        params.delete("search");
      }
      params.set("page", 1);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fetchReportData = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", currentPage.toString());
    params.set("limit", perPage.toString());
    if (keyword) params.set("search", keyword);
    if (status !== "all") params.set("status", status);
    if (customFromDate)
      params.set("fromDate", moment(customFromDate).format("MM-DD-YYYY"));
    if (customToDate)
      params.set("toDate", moment(customToDate).format("MM-DD-YYYY"));
    setSearchParams(params, { replace: true });
  }, [
    currentPage,
    perPage,
    keyword,
    status,
    customFromDate,
    customToDate,
    setSearchParams,
  ]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  useEffect(() => {
    if (error) {
      setErrorMessage(
        error?.data?.message || "Failed to fetch vehicle loading status."
      );
    }
    if (
      customFromDate &&
      customToDate &&
      differenceInDays(customToDate, customFromDate) > 90
    ) {
      setCustomToDate(addDays(customFromDate, 90));
      setErrorMessage("Date range adjusted to maximum 90 days.");
    }
  }, [error, customFromDate, customToDate]);

  const handleClearDate = (field) => {
    if (field === "fromDate") {
      setCustomFromDate(null);
    } else if (field === "toDate") {
      setCustomToDate(null);
    }
    handlePageChange(1);
    fetchReportData();
  };

  useAppMeta(
    `Vehicles Loading Status${keyword ? ` - Search: ${keyword}` : ""}`
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={errorMessage || error?.data?.message} />
        <BreadcrumbLayout.Paper>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: 2,
              alignItems: "center",
              px: 2,
              pt: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ flexShrink: 0 }}>
              Vehicle Loading Status
              {keyword && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (Searching: "{keyword}")
                </Typography>
              )}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 2,
                alignItems: "center",
              }}
            >
              <BaseSelect
                label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  handlePageChange(1);
                }}
                options={[
                  { label: "All Status", value: "all" },
                  { label: "Loaded", value: "loaded" },
                  { label: "Not Loaded", value: "not loaded" },
                ]}
                getOptionLabel={(opt) => opt.label}
                getOptionValue={(opt) => opt.value}
                size="small"
                sx={{ minWidth: 140 }}
              />
              <Box>
                <DatePicker
                  label="From Date"
                  value={customFromDate}
                  onChange={(newValue) => {
                    setCustomFromDate(newValue);
                    if (
                      customToDate &&
                      differenceInDays(customToDate, newValue) > 90
                    ) {
                      setCustomToDate(addDays(newValue, 90));
                      setErrorMessage(
                        "Date range adjusted to maximum 90 days."
                      );
                    }
                    handlePageChange(1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{ width: 200 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {customFromDate && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearDate("fromDate")}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  maxDate={customToDate || new Date()}
                  disableFuture
                />

                <DatePicker
                  label="To Date"
                  value={customToDate}
                  onChange={(newValue) => {
                    setCustomToDate(newValue);
                    if (
                      customFromDate &&
                      differenceInDays(newValue, customFromDate) > 90
                    ) {
                      setCustomFromDate(addDays(newValue, -90));
                      setErrorMessage(
                        "Date range adjusted to maximum 90 days."
                      );
                    }
                    handlePageChange(1);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={{ width: 200 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {customToDate && (
                              <IconButton
                                size="small"
                                onClick={() => handleClearDate("toDate")}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  minDate={customFromDate}
                  maxDate={new Date()}
                  disableFuture
                />
              </Box>

              <TableFilter
                searchLabel="Vehicle ID / Registration Number"
                disabled={isLoading || isFetching}
                initialFilters={2}
                searchValue={keyword}
                onSearch={handleSearch}
              />
            </Box>
          </Box>
          <AppTable
            columns={columns}
            data={data?.vehicles || []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={data?.totalItems || 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            subHeader={false}
            responsive
            highlightOnHover
          />

          <Snackbar
            open={!!errorMessage}
            autoHideDuration={6000}
            onClose={() => setErrorMessage("")}
          >
            <Alert severity="error" onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default ShipmentLoadingByVehicleList;
